/**
 * legacyUploadCompat.js — LEGACY_DECOMMISSIONED quarantine gate.
 *
 * Single source of truth for whether the legacy upload subsystem's
 * automatic / global / on-event recovery behaviour is permitted to
 * run.
 *
 * Default: OFF.
 *   • All pageshow / focus / visibilitychange / pagehide listeners
 *     registered by legacy upload code MUST short-circuit when this
 *     returns false.
 *   • Auto-reopen, picker recovery, interrupted-upload resurrection,
 *     basket resurrection, server-state refetch on bfcache restore —
 *     all gated by this single flag.
 *
 * Enabled: only when `REACT_APP_LEGACY_UPLOAD_COMPAT=true` is set
 * AT BUILD TIME. There is no runtime toggle. There is no auto-detect.
 * There is no "smart" wake-up. The flag is the only switch.
 *
 * Operator doctrine (May 22, 2026):
 *   The legacy upload architecture is permanently decommissioned.
 *   It must not silently resurrect through hidden listeners,
 *   recovery hooks, storage keys, or route logic.
 *
 * Migration path:
 *   The new Video Analysis Engine (VAE — separate clean-room build)
 *   is the active path. Legacy code remains in the bundle but its
 *   ghost listeners are neutered until explicitly opted in via the
 *   flag — which exists ONLY for an emergency rollback scenario.
 *
 * See: /app/memory/LEGACY_UPLOAD_DEPENDENCY_MAP.md
 */

let _warned = false;

export function legacyUploadCompatEnabled() {
  // Read once; build-time env var. Returns the boolean each call so
  // callers can inline it without caching.
  const v = process.env.REACT_APP_LEGACY_UPLOAD_COMPAT;
  const on = v === "true" || v === "1";
  if (on && !_warned && typeof console !== "undefined") {
    // eslint-disable-next-line no-console
    console.warn(
      "[legacy-upload-compat] ENABLED — automatic recovery " +
      "listeners (pageshow / focus / visibilitychange / pagehide) " +
      "are armed for the legacy upload subsystem. This is an " +
      "emergency rollback mode; the new Video Analysis Engine is " +
      "the supported path.",
    );
    _warned = true;
  }
  return on;
}

/**
 * Compose with any existing `enabled` gate. Returns true ONLY when
 * BOTH the caller's local gate is true AND legacy compat is enabled.
 *
 * Usage:
 *   useEffect(() => {
 *     if (!gatedByLegacyUploadCompat(myLocalEnabled)) return;
 *     window.addEventListener("pageshow", handler);
 *     …
 *   });
 */
export function gatedByLegacyUploadCompat(localEnabled = true) {
  return Boolean(localEnabled) && legacyUploadCompatEnabled();
}
